import { NegativeColors, PositiveColors } from '@picsart/design-system/foundation/colors';
import { FontSizes, LineHeights } from '@picsart/design-system/foundation/typography';

import { CornerRadius } from '@picsart/design-system/foundation/cornerRadius';
import { BreakPointsMedia, BreakpointValues } from '@picsart/design-system/foundation/responsive';
import { Spacings } from '@picsart/design-system/foundation/spacings';
import { ignoreRTL } from '@picsart/design-system/foundation/styleHelpers';
import { createUseStyles } from 'react-jss';
import { mediaQuery } from '@picsart/rc/helpers';
import { ColorSchemes } from 'redesignedComponents/context/colorScheme/enums';
import type { ColorSchemeType } from 'redesignedComponents/context/colorScheme/types';

type StyleProps = {
  reversed?: boolean;
  colorScheme?: ColorSchemeType;
};

const useStyles = createUseStyles<string, StyleProps>(
  () => ({
    root: ({ colorScheme }) => ({
      width: '100%',
      height: 'auto',
      backgroundColor:
        colorScheme === ColorSchemes.LIGHT ? 'transparent' : NegativeColors.base.default,
    }),
    regularSize: {
      fontSize: FontSizes.s42,
      lineHeight: LineHeights.l50,
    },

    wrapper: {
      '&&': {
        display: 'grid',
        gridTemplateRows: '1fr',
        gridTemplateColumns: '6fr 6fr',
        alignItems: 'center',
        gridGap: Spacings.s44,
        width: '100%',
        boxSizing: 'border-box',
        margin: 'auto',
        paddingBottom: Spacings.s64,
        gridTemplateAreas: ({ reversed }) => (reversed ? '"image content"' : '"content image"'),

        [BreakPointsMedia.SM]: {
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
          margin: [Spacings.s0, 'auto'],
          maxWidth: '100%',
          gridGap: Spacings.s16,
          paddingBottom: Spacings.s16,
          [ignoreRTL]: {
            gridTemplateColumns: '1fr',
            gridTemplateAreas: "'image' 'content'",
          },
        },
        [BreakPointsMedia.XS]: {
          margin: [Spacings.s0, 'auto'],
          maxWidth: '100%',
          gridGap: Spacings.s16,
          paddingBottom: Spacings.s16,
          [ignoreRTL]: {
            gridTemplateColumns: '1fr',
            gridTemplateAreas: "'image' 'content'",
          },
        },
        [BreakPointsMedia.XXS]: {
          margin: [Spacings.s0, 'auto'],
          maxWidth: '100%',
          gridGap: Spacings.s16,
          paddingBottom: Spacings.s16,
          [ignoreRTL]: {
            gridTemplateColumns: '1fr',
            gridTemplateAreas: "'image' 'content'",
          },
        },
      },
    },
    howToContent: {
      maxWidth: 600,
      width: '100%',
      gridArea: 'content',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',

      [BreakPointsMedia.SM]: {
        width: '100%',
        margin: [Spacings.s0, 'auto'],
        gridArea: 'content',
        padding: [Spacings.s0, Spacings.s12],
      },
      [BreakPointsMedia.XS]: {
        width: '100%',
        margin: [Spacings.s0, 'auto'],
        gridArea: 'content',
        padding: [Spacings.s0, Spacings.s12],
      },
      [BreakPointsMedia.XXS]: {
        width: '100%',
        margin: [Spacings.s0, 'auto'],
        gridArea: 'content',
        padding: [Spacings.s0, Spacings.s12],
      },
    },
    title: {
      color: props =>
        props.colorScheme === ColorSchemes.LIGHT
          ? PositiveColors.text.default
          : NegativeColors.text.default,
    },
    itemsList: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      marginTop: Spacings.s32,
      minHeight: 450,
      '@media screen and (max-width: 425px)': { minHeight: 480 },
    },

    mediaHolder: {
      position: 'relative',
      gridArea: 'image',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100%',

      [mediaQuery(1023)]: {
        maxWidth: '100%',
        width: '100%',
      },
    },
    media: {
      // TODO Fix the issue in Growth RC and remove this block
      '& > video': {
        opacity: '1 !important',
      },
    },
    buttonHolder: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 1,
    },

    // Skeleton styles
    skeletonRoot: {
      extend: 'root',
      minHeight: 600,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      [mediaQuery(BreakpointValues.MD)]: {
        display: 'block',
      },
    },
    skeletonTitle: {
      width: 100,
      height: 144,
    },
    sekeletonItemWrapper: {
      width: 100,
      display: 'flex',
      justifyContent: 'center',
    },
    sekeletonListItem: {
      width: 100,
      display: 'flex',
      flexDirection: 'column',
      marginTop: Spacings.s24,
    },
    skeletonListTitle: {
      height: Spacings.s24,
    },
    skeletonListDescription: {
      marginTop: Spacings.s8,
      height: Spacings.s56,
    },
    skeletonButton: {
      width: 180,
      height: Spacings.s48,
      borderRadius: CornerRadius.r24,
      marginTop: Spacings.s12,
      alignSelf: 'center',
    },
    imageSkeleton: {
      width: '100%',
      height: '100%',
    },
  }),
  {
    name: 'how-to',
  },
);

export default useStyles;
